import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useCompanyData,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { updateWorkRequest } from '@commenda/api/workRequest/updateWorkRequest';
import { PlusIcon, ProceedIcon } from '@commenda/icons';
import {
  Button,
  InputErrorMessage,
  Loading,
  LoadingIndicator,
  MultiSelect,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  DirectorPersonalDetails,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { DirectorCardKS, IncorporationFormHeading } from '../../common';
import { getDirectorName } from '../../Director/common';
import DirectorModal from '../DirectorModal';
import { CommendaResidentDirectorForm } from './CommendaResidentDirectorForm';
import DirectorForm from './DirectorForm';
import { validateDirectorInfo } from './validators';

const DirectorInfo = ({
  next,
  previous,
  data,
  mutate,
  companyId,
  corporationId,
  token,
  setNotification,
  resetFurtherSteps,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  companyId: number;
  corporationId: number;
  token: string;
  setNotification: TSetNotification;
  resetFurtherSteps: () => void;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  // All the directors in the company
  const [directors, setDirectors] = useState<DirectorBase[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const {
    data: companyData,
    error,
    mutate: companyMutate,
  } = useGetAllDirectorsInCompany(companyId);

  // Get corporations data for cross-entity email validation
  const { corporations } = useCompanyData(companyId);

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const selectedDirectorIdsRef = useRef<number[]>([]);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const [optForIndianResidentDirector, setOptForIndianResidentDirector] =
    useState(data?.requestData?.optForIndianResidentDirector || false);
  const [
    optForIndianResidentDirectorError,
    setOptForIndianResidentDirectorError,
  ] = useState<string | undefined>(undefined);

  useEffect(() => {
    setDirectors(
      companyData?.directors?.map((eachDirector) => ({
        id: eachDirector?.id,
        directorFirstName: eachDirector.directorFirstName,
        directorMiddleName: eachDirector.directorMiddleName,
        directorLastName: eachDirector.directorLastName,
        email: eachDirector.email,
        operatingAddress: eachDirector.operatingAddress,
        personalDetails: {
          nationality: (eachDirector.personalDetails as DirectorPersonalDetails)
            ?.nationality as Country,
          hasDSC: (eachDirector.personalDetails as DirectorPersonalDetails)
            ?.hasDSC,
          educationalQualification: (
            eachDirector.personalDetails as DirectorPersonalDetails
          )?.educationalQualification,
          occupation: eachDirector?.personalDetails?.occupation,
          placeOfBirth: eachDirector?.personalDetails?.placeOfBirth,
          dateOfOccupancyInCurrentAddress:
            eachDirector.personalDetails?.dateOfOccupancyInCurrentAddress,
        },
        addressProof: eachDirector?.addressProof,
        aadharCard: eachDirector?.aadharCard,
        panCard: eachDirector?.panCard,
        number: eachDirector.number,
        corporationHasDirector: eachDirector.corporationHasDirector,
        directorIdentificationNumber: eachDirector.directorIdentificationNumber,
        directorFiles: eachDirector.directorFiles,
      }))
    );
  }, [companyData]);

  useEffect(() => {
    const alreadySelectedDirectors = [...selectedDirectorIdsRef.current];

    companyData?.directors
      ?.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation?.isDirector
        )
      )
      .forEach((director) => {
        if (director?.WorkRequests?.find((wrkReq) => wrkReq.id === id)) {
          if (!alreadySelectedDirectors.includes(director.id))
            alreadySelectedDirectors.push(director.id);
        }
      });

    setSelectedDirectorIds(alreadySelectedDirectors);
  }, [companyData]);

  if (!companyData) {
    if (error) {
      console.error(error);
    }
    return <Loading />;
  }

  const onSubmit = async () => {
    setSubmitting(true);

    if (!data?.id) {
      setNotification({
        show: true,
        valid: false,
        message: 'Work request ID is missing. Please refresh the page.',
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    if (
      (selectedDirectorIds.length < 2 && !optForIndianResidentDirector) ||
      (selectedDirectorIds.length === 0 && optForIndianResidentDirector)
    ) {
      setNotification({
        show: true,
        valid: false,
        message: `Indian Private Limited entity requires you to have at least two directors if not opting to use Commenda's Indian Resident Director service.`,
        title: 'Error',
      });

      setOptForIndianResidentDirectorError(
        "Please select at least two directors if not opting to use Commenda's Indian Resident Director service. If you are opting to use Commenda's Indian Resident Director service, please select at least one director."
      );

      setSubmitting(false);

      return;
    }

    // Validate email uniqueness
    const companyEmail = data?.corporation?.contactEmail;
    const selectedDirectors = directors?.filter((director) =>
      selectedDirectorIds.includes(director.id)
    );
    const directorEmails =
      selectedDirectors
        ?.map((director) => director.email?.trim()?.toLowerCase())
        .filter(Boolean) || [];

    if (companyEmail) {
      const allEmails = [companyEmail.trim().toLowerCase(), ...directorEmails];
      const duplicateEmails = allEmails.filter(
        (email, index) => allEmails.indexOf(email) !== index
      );

      if (duplicateEmails.length > 0) {
        setNotification({
          show: true,
          valid: false,
          message:
            'Email addresses of the company and the directors must be unique.',
          title: 'Error',
        });
        setSubmitting(false);
        return;
      }
    }

    const formError = validateDirectorInfo(
      directors,
      selectedDirectorIds,
      corporationId
    );

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));

      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for directors by clicking on "Edit".`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const doesAtLeastOneDirectorHaveIndianResidence = directors.some(
      (dir) => dir?.operatingAddress?.country === Country.IN
    );

    if (
      !optForIndianResidentDirector &&
      !doesAtLeastOneDirectorHaveIndianResidence
    ) {
      setNotification({
        show: true,
        valid: false,
        message: `At least one director must have Indian residence. Please select at least one director with Indian residence or opt for Commenda's Indian Resident Director service.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const directorsToDisconnect = companyData?.directors
      ?.filter((eachDirector) =>
        eachDirector?.corporationHasDirector?.find(
          (dirWithCorp) =>
            dirWithCorp.corporationId === data.corporationId &&
            dirWithCorp.directorId === eachDirector.id &&
            dirWithCorp.additionalDirectorInformation?.isDirector &&
            !selectedDirectorIds.includes(dirWithCorp.directorId)
        )
      )
      ?.map((each) => each.id);

    const directorUpdates: UpdateMultipleDirectorInputType[] = [
      ...selectedDirectorIds.map((eachDirId) => ({
        id: eachDirId,
        wrkReqId: data.id,
        corporationHasDirector: [
          {
            corporationId: data.corporationId,
            directorId: eachDirId,
            additionalDirectorInformation: {
              isDirector: true,
            },
          },
        ],
      })),
      ...directorsToDisconnect.map((eachDirId) => ({
        id: eachDirId,
        workRequestIdToDisconnect: data.id,
        corporationIdsToDisconnect: [data.corporationId],
      })),
    ];

    const directorUpdatePromise = updateMultipleDirectors(
      directorUpdates,
      token
    );

    const workRequestUpdatePromise = updateWorkRequest({
      data: {
        requestData: {
          optForIndianResidentDirector,
        },
      },
      token,
      workRequestId: data.id,
    });

    const [directorResponse, workRequestResponse] = await Promise.all([
      directorUpdatePromise,
      workRequestUpdatePromise,
    ]);

    if (!directorResponse || !workRequestResponse) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });

      setSubmitting(false);

      return;
    }

    await mutate();

    next();

    setSubmitting(false);
  };

  const hasDirectors = companyData?.directors?.length;

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text="Add Director(s)"
        additionalText="Please provide information about this corporation's designated directors. At least two directors are required if not opting to use Commenda's Indian Resident Director service."
      />

      {hasDirectors ? (
        <MultiSelect
          id="add-director-multiselect"
          options={(companyData?.directors || []).map((dir) => ({
            label: getDirectorName(dir),
            value: dir.id.toString(),
          }))}
          onValueChange={(val: string[]) => {
            setSelectedDirectorIds(val.map(Number));
            selectedDirectorIdsRef.current = val.map(Number);
            resetFurtherSteps();
            setOptForIndianResidentDirectorError(undefined);
          }}
          defaultValue={selectedDirectorIds.map(String)}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            onAddItem: () => setDirectorModalShown(true),
            placeholder: 'Add new director',
          }}
          placeholder="Add or select directors"
        />
      ) : (
        <Button
          variant="secondary"
          className="w-full flex gap-2 items-center justify-center"
          onClick={() => setDirectorModalShown(true)}
        >
          <PlusIcon className="text-neutral-1" />
          <span>Add Director</span>
        </Button>
      )}

      <div>
        {directors
          ?.filter((dir) => selectedDirectorIds.includes(dir.id))
          .map((director) => (
            <DirectorCardKS
              directorForm={DirectorForm}
              key={director.id}
              director={director}
              companyDataMutate={companyMutate}
              setNotification={setNotification}
              corporationId={corporationId}
              companyId={companyId}
              token={token}
              hasError={directorIdsWithError.has(director.id)}
              onErrorResolve={() =>
                setDirectorIdsWithError((prevIds) => {
                  const newIds = new Set(prevIds);
                  newIds.delete(director.id);
                  return newIds;
                })
              }
              onRemoveDirector={async (directorId: number) => {
                setSelectedDirectorIds((prev) =>
                  prev.filter((dirId) => dirId !== director.id)
                );
                selectedDirectorIdsRef.current = selectedDirectorIds;
                resetFurtherSteps();
                setOptForIndianResidentDirectorError(undefined);
              }}
              allCorporations={corporations}
              workRequestData={data}
            />
          ))}
        {optForIndianResidentDirectorError && (
          <div className="mt-3">
            <InputErrorMessage
              id="optForResidentDirector-error"
              message={optForIndianResidentDirectorError}
            />
          </div>
        )}
      </div>

      <div className="max-w-[700px]">
        <DirectorModal
          isOpen={isDirectorModalShown}
          setOpen={setDirectorModalShown}
          companyDataMutate={companyMutate}
          DirectorForm={DirectorForm}
          setNotification={setNotification}
          companyId={companyId}
          corporationId={corporationId}
          token={token}
          selectedDirectorIds={selectedDirectorIds}
          setSelectedDirectorIds={setSelectedDirectorIds}
          modalConfig={{
            isDirector: true,
            postSave: (dir) => {
              if (!selectedDirectorIdsRef.current.includes(dir.id)) {
                selectedDirectorIdsRef.current.push(dir.id);
              }
              setOptForIndianResidentDirectorError(undefined);
            },
          }}
          workRequestData={data}
          allCorporations={corporations}
        />
      </div>

      <CommendaResidentDirectorForm
        setOptForResidentDirector={setOptForIndianResidentDirector}
        optForResidentDirector={optForIndianResidentDirector}
        setOptForResidentDirectorError={setOptForIndianResidentDirectorError}
      />

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || directors?.length === 0}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span className={classNames(isSubmitting ? 'ml-2' : '')}>
            Continue
          </span>
          {!isSubmitting && <ProceedIcon className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default DirectorInfo;
